import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Import translation resources
import enCommon from "../locales/en/common.json";
import enAbout from "../locales/en/about.json";
import enFaq from "../locales/en/faq.json";
import enHome from "../locales/en/home.json";
import enTrust from "../locales/en/trust.json";
import enSeo from "../locales/en/seo.json";
import enTestimonials from "../locales/en/testimonials.json";
import enPrivacy from "../locales/en/privacy.json";
import enSmsHelp from "../locales/en/sms-help.json";
import enSmsPrivacy from "../locales/en/sms-privacy.json";
import enSmsTerms from "../locales/en/sms-terms.json";
import enFieldNotes from "../locales/en/field-notes.json";
import enPress from "../locales/en/press.json";
import enTermsOfUse from "../locales/en/terms-of-use.json";
import enInvoicingPaymentTerms from "../locales/en/invoicing-payment-terms.json";
import enUsecase from "../locales/en/usecase.json";
import enCrittora from "../locales/en/crittora.json";
import enWireInstructions from "../locales/en/wire-instructions.json";
import enNotFound from "../locales/en/not-found.json";
import enMortgageAiGovernance from "../locales/en/mortgage-ai-governance.json";
import enMortgageEnterprise from "../locales/en/mortgage-enterprise.json";
import enMortgageAiBuilder from "../locales/en/mortgage-ai-builder.json";
import enMortgageConsultancies from "../locales/en/mortgage-consultancies.json";

import esCommon from "../locales/es/common.json";
import esAbout from "../locales/es/about.json";
import esFaq from "../locales/es/faq.json";
import esHome from "../locales/es/home.json";
import esTrust from "../locales/es/trust.json";
import esSeo from "../locales/es/seo.json";
import esTestimonials from "../locales/es/testimonials.json";
import esPrivacy from "../locales/es/privacy.json";
import esSmsHelp from "../locales/es/sms-help.json";
import esSmsPrivacy from "../locales/es/sms-privacy.json";
import esSmsTerms from "../locales/es/sms-terms.json";
import esFieldNotes from "../locales/es/field-notes.json";
import esPress from "../locales/es/press.json";
import esTermsOfUse from "../locales/es/terms-of-use.json";
import esInvoicingPaymentTerms from "../locales/es/invoicing-payment-terms.json";
import esUsecase from "../locales/es/usecase.json";
import esCrittora from "../locales/es/crittora.json";
import esWireInstructions from "../locales/es/wire-instructions.json";
import esNotFound from "../locales/es/not-found.json";
import esMortgageAiGovernance from "../locales/es/mortgage-ai-governance.json";
import esMortgageEnterprise from "../locales/es/mortgage-enterprise.json";
import esMortgageAiBuilder from "../locales/es/mortgage-ai-builder.json";
import esMortgageConsultancies from "../locales/es/mortgage-consultancies.json";

const resources = {
  en: {
    common: enCommon,
    about: enAbout,
    faq: enFaq,
    home: enHome,
    trust: enTrust,
    seo: enSeo,
    testimonials: enTestimonials,
    privacy: enPrivacy,
    "sms-help": enSmsHelp,
    "sms-privacy": enSmsPrivacy,
    "sms-terms": enSmsTerms,
    "field-notes": enFieldNotes,
    press: enPress,
    "terms-of-use": enTermsOfUse,
    "invoicing-payment-terms": enInvoicingPaymentTerms,
    usecase: enUsecase,
    crittora: enCrittora,
    "wire-instructions": enWireInstructions,
    "not-found": enNotFound,
    "mortgage-ai-governance": enMortgageAiGovernance,
    "mortgage-enterprise": enMortgageEnterprise,
    "mortgage-ai-builder": enMortgageAiBuilder,
    "mortgage-consultancies": enMortgageConsultancies,
  },
  es: {
    common: esCommon,
    about: esAbout,
    faq: esFaq,
    home: esHome,
    trust: esTrust,
    seo: esSeo,
    testimonials: esTestimonials,
    privacy: esPrivacy,
    "sms-help": esSmsHelp,
    "sms-privacy": esSmsPrivacy,
    "sms-terms": esSmsTerms,
    "field-notes": esFieldNotes,
    press: esPress,
    "terms-of-use": esTermsOfUse,
    "invoicing-payment-terms": esInvoicingPaymentTerms,
    usecase: esUsecase,
    crittora: esCrittora,
    "wire-instructions": esWireInstructions,
    "not-found": esNotFound,
    "mortgage-ai-governance": esMortgageAiGovernance,
    "mortgage-enterprise": esMortgageEnterprise,
    "mortgage-ai-builder": esMortgageAiBuilder,
    "mortgage-consultancies": esMortgageConsultancies,
  },
};

// Keep bundles in sync during dev HMR when the singleton is already initialized.
if (i18n.isInitialized) {
  (Object.entries(resources) as Array<[string, Record<string, unknown>]>).forEach(
    ([language, namespaces]) => {
      Object.entries(namespaces).forEach(([namespace, bundle]) => {
        i18n.addResourceBundle(language, namespace, bundle, true, true);
      });
    }
  );
}

// Only initialize if not already initialized
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: "en",
      debug: true, // Enable debug mode to see what's happening
      supportedLngs: ["en", "es"],
      nonExplicitSupportedLngs: true,

      // Language detection options - simplified for static generation
      detection: {
        order: ["path", "localStorage", "navigator", "htmlTag"],
        caches: ["localStorage"],
        lookupFromPathIndex: 0,
      },

      interpolation: {
        escapeValue: false, // React already does escaping
      },

      returnNull: false,

      // Namespace configuration
      defaultNS: "common",
      ns: [
        "common",
        "about",
        "faq",
        "home",
        "trust",
        "seo",
        "testimonials",
        "privacy",
        "sms-help",
        "sms-privacy",
        "sms-terms",
        "field-notes",
        "press",
        "terms-of-use",
        "invoicing-payment-terms",
        "usecase",
        "crittora",
        "wire-instructions",
        "not-found",
        "mortgage-ai-governance",
        "mortgage-enterprise",
        "mortgage-ai-builder",
        "mortgage-consultancies",
      ],

      // Static generation compatibility
      react: {
        useSuspense: false,
      },

      // Ensure translations are loaded synchronously
      initImmediate: false,

      // Load all namespaces for the current language
      load: "all",

      // Preload all namespaces
      preload: ["en", "es"],
    });
}

export default i18n;
